// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import { Modal } from 'components/atoms/Modal';
import { useTranslation } from 'react-i18next';
import styles from './ConfirmMFAModal.module.scss';
import { Form } from 'react-final-form';
import { validateRequiredField } from 'util/validator';
import { InputDataListField } from 'components/molecules/InputDataListField';
import { useEffect, useRef, useState } from 'react';
import { mergeClasses } from 'util/commons';

interface ConfirmMFAModalProps {
  email: string;
  onClose: () => void;
  onSubmit: (code: string) => void;
  onResendCode: () => Promise<void>;
  defaultResendCountDown?: number;
  error?: string;
}

const ConfirmMFAModal: React.FC<ConfirmMFAModalProps> = ({
  onClose,
  onSubmit,
  onResendCode,
  email,
  defaultResendCountDown = 60,
  error,
}) => {
  const { t } = useTranslation();
  const [countdownId, setCountdownId] = useState<string | null>(
    crypto.randomUUID()
  );
  const [countdown, setCountdown] = useState(defaultResendCountDown);
  const timerRef = useRef<NodeJS.Timer>();

  useEffect(() => {
    if (countdownId == null) {
      clearInterval(timerRef.current);

      return;
    }

    setCountdown(defaultResendCountDown);

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        const newVal = prev - 1;

        if (newVal > 0) {
          return newVal;
        }

        clearInterval(timerRef.current);
        setCountdownId(null);

        return 0;
      });
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
    };
  }, [countdownId]);

  return (
    <div className={styles.confirmMFAModal}>
      <Modal>
        <div className={styles.modalContent}>
          <div className={styles.customTitle}>
            <p className={styles.descriptionTitle}>
              {t('confirm_mfa_modal.title')}
            </p>
            <div className={styles.top} onClick={onClose}>
              <IconCloseTablet />
            </div>
          </div>
        </div>

        <div className={styles.modalBody}>
          <Form<{
            code: string;
          }>
            onSubmit={(values) => {
              onSubmit(values.code);
            }}
            validate={(values) => {
              return {
                code: validateRequiredField(
                  values.code,
                  t('confirm_mfa_modal.label_mfa_code') || ''
                ),
              };
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit} className={styles.formInput}>
                <p className={styles.description}>
                  {t('confirm_mfa_modal.description', {
                    email,
                  }) || ''}
                </p>

                <div className={styles.inputFrame}>
                  <InputDataListField
                    label={t('confirm_mfa_modal.label_mfa_code') || ''}
                    placeholder={t('confirm_mfa_modal.label_mfa_code') || ''}
                    name="code"
                    required
                    maxlength={100}
                  />
                </div>

                {error && <p className={styles.error}>{error}</p>}

                <div className={styles.submit}>
                  <button
                    disabled={props.invalid}
                    className={styles.submitButton}
                    type="submit"
                  >
                    {t('confirm_mfa_modal.button_submit')}
                  </button>
                </div>

                <p className={styles.receiveQuestion}>
                  <span>{t('confirm_mfa_modal.receive_question')}</span>

                  <span
                    className={mergeClasses(styles.resendCode, {
                      [styles.resendCodeDisabled]: countdown > 0,
                    })}
                    onClick={() =>
                      onResendCode().then(() =>
                        setCountdownId(crypto.randomUUID())
                      )
                    }
                  >
                    {t('confirm_mfa_modal.resend_code')}
                  </span>

                  {countdown > 0 && (
                    <span>
                      {' '}
                      {`(${t('confirm_mfa_modal.please_wait', {
                        time: countdown.toString().padStart(2, '0'),
                      })})`}
                    </span>
                  )}
                </p>
              </form>
            )}
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default ConfirmMFAModal;
